import React from 'react';
import { EventDocument } from 'interfaces/document.interface';
import EventDocumentFileName from './eventDocumentFileName.component';
import { DownloadIcon, RootState, UUID } from '@umanforge/common';
import Button from '@common_components/buttons/button.component';
import { TrashIcon } from '@common_components/icons/trash.icon';
import { EventService } from 'services/events.service';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { VisualisationService } from 'services/visualisation.service';


interface EventDocumentBodyProps {
  file: EventDocument;
  children?: JSX.Element;
  isViewMode?: boolean;
  canDelete: boolean;
  isDeleted: boolean;
  onClickFile?: () => void;
  deleteFile: ( fileId: UUID ) => void;
}

const EventDocumentBody: React.FC<EventDocumentBodyProps> = ( { file, canDelete, isDeleted, isViewMode, children, onClickFile, deleteFile } ) => {
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );

  return (
    <div
      className={`
        flex flex-col w-full h-full gap-2 p-0.5
        items-center justify-center text-center
        max-w-[160px] max-h-[200px] min-w-[95px] min-h-[160px]
        rounded-md border border-orange-base-light shadow shadow-gray-400
        ${ isDeleted ? 'bg-red-100' : '' }
        transition ease-in-out delay-50
        ${ onClickFile && 'hover:-translate-y-1 hover:scale-110 hover:shadow-xl hover:shadow-gray-400' }
      `}
    >
      <div
        className={`
          flex w-full h-[60%] justify-center select-none
          ${ onClickFile && 'cursor-pointer' }
        `}
        onClick={ () => {
          if ( onClickFile ) {
            onClickFile();
          }
        } }
      >
        { children }
      </div>

      <div className='flex flex-col w-full h-fit gap-0.5 justify-end'>
        <div className='flex h-fit px-1 sm:px-2 md:px-4 justify-around'>
          <Button
            icon={ <DownloadIcon className='w-[18px] h-[18px]'/> }
            color='bg-download-color'
            colorHover='hover:bg-download-color-hover'
            hoverBorderColor='hover:border-download-color-hover'
            textColor='text-white'
            textColorHover='hover:text-white'
            onClick={ async () => {
              let blob = null;
              if ( isViewMode ) {
                blob = await VisualisationService.downloadDocument( file.id, visualisationToken, dispatch );
              } else {
                blob = await EventService.downloadDocument( file.id, token, dispatch );
              }
              const url = window.URL.createObjectURL( blob );

              const tempLink = document.createElement( 'a' );
              tempLink.href = url;
              tempLink.setAttribute(
                'download',
                file.originalName
              );
              document.body.appendChild( tempLink );
              tempLink.click();
              document.body.removeChild( tempLink );
              window.URL.revokeObjectURL( url );
            } }
          />
          {
            canDelete === true ?
              <Button
                icon={ isDeleted ? <PlusIcon className='w-[18px] h-[18px]'/> : <TrashIcon className='w-[18px] h-[18px]'/> }
                color={ isDeleted ? 'bg-green-500' : 'bg-red-500' }
                colorHover={ isDeleted ? 'hover:bg-green-600' : 'hover:bg-red-600' }
                hoverBorderColor={ isDeleted ? 'hover:border-green-600' : 'hover:border-red-600' }
                textColor='text-white'
                textColorHover='hover:text-white'
                onClick={ () => { deleteFile( file.id ); } }
              />
              :
              null
          }
        </div>
        <div className='w-full h-fit'>
          <EventDocumentFileName file={ file }/>
        </div>
      </div>

    </div>
  );
};

export default EventDocumentBody;
